import { clearOrderEntryCollections } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/clear-order-entry-collections';
import { getLocale } from '@/utils/i18n';
import { inArray, or, useLiveQuery } from '@platform/db/tanstack-db';
import { NetworkStatus } from '@apollo/client';
import { ORDER_ENTRY_ITEMS_LIMIT } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import { orderEntryCollection } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/order-entry-collection';
import {
  OrderEntryOrderGuideItemFilter,
  OrderEntryPriceEditDisplayHeader,
} from '@/types';
import {
  type OrderEntrySyncParams,
  useOrderEntrySync,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/sync/use-order-entry-sync';
import { SalesRepConfig } from '@domain/configuration/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGeneratedOrderEntryDataQuery } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/documents/order-entry-data.generated';
import { useIsFocused } from '@react-navigation/native';

/**
 * Parameters for {@link useOrderEntryDataLoader}.
 */
export interface UseOrderEntryDataLoaderParams {
  /** Fulfillment delivery date (ISO string). */
  deliveryDate: string;
  /** Optional linked supplier collection UUID for restaurant collections linked to a supplier collection. */
  linkedSupplierCollectionUUID?: string;
  /** Optional order guide UUID to filter table data. */
  orderGuideUUID?: string;
  /** Restaurant UUID. */
  restaurantUUID: string;
  /** Sales rep config for building PriceEditorConfigs during sync. */
  salesRepConfig: SalesRepConfig | null;
  /** Saved fulfillment selection for the current restaurant. */
  savedSelectedFulfillment: OrderEntrySyncParams['fulfillment'];
  /** Supplier timezone (IANA). */
  supplierTimezone: string;
  /** Supplier UUID. */
  supplierUUID: string;
}

/**
 * Fetches cart and order entry table in one request and syncs to cart store and collections when ready.
 * Loads cart + default order guide in one request (replaces prior separate cart loader + sync useEffect).
 *
 * @param params - Loader parameters (see {@link UseOrderEntryDataLoaderParams}).
 * @returns Loading state, error, refetch, table data (from collection), and price edit headers.
 */
export function useOrderEntryDataLoader({
  deliveryDate,
  linkedSupplierCollectionUUID,
  orderGuideUUID,
  restaurantUUID,
  salesRepConfig,
  savedSelectedFulfillment,
  supplierTimezone,
  supplierUUID,
}: UseOrderEntryDataLoaderParams) {
  const {
    synchronizeOrderEntryCollections,
    synchronizeCartStore,
    synchronizePriceEditors,
  } = useOrderEntrySync();
  const locale = getLocale();

  const { data, loading, error, networkStatus, refetch } =
    useGeneratedOrderEntryDataQuery({
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: {
        deliveryDate,
        limit: ORDER_ENTRY_ITEMS_LIMIT,
        locale,
        orderGuideItemFilter: OrderEntryOrderGuideItemFilter.All,
        restaurantUUID,
        supplierUUID,
      },
    });

  // The collections and price editors outlive this hook. Drop the previous
  // restaurant's rows when the context changes, and everything on unmount, so
  // rows do not accumulate across restaurant switches within one session.
  useEffect(() => clearOrderEntryCollections, [restaurantUUID, supplierUUID]);

  const paramsRef = useRef<OrderEntrySyncParams | null>(null);
  // Only sync when we have a saved fulfillment: without it we have no cart/context to merge into,
  // so we keep params null and short-circuit in the effect below.
  paramsRef.current =
    savedSelectedFulfillment !== null
      ? {
          fulfillment: savedSelectedFulfillment,
          restaurantUUID,
          supplierTimezone,
          supplierUUID,
        }
      : null;

  // Track whether fetched data has been synced to collections so the skeleton stays visible
  // until useLiveQuery can return rows for the current order guide.
  const [isSyncing, setIsSyncing] = useState(false);
  const syncedDataRef = useRef<typeof data>(undefined);

  // Mark as syncing when new data arrives that hasn't been synced yet.
  if (
    data &&
    data !== syncedDataRef.current &&
    !loading &&
    !isSyncing &&
    paramsRef.current !== null
  ) {
    setIsSyncing(true);
  }

  const isFocused = useIsFocused();
  const syncedEditorsRef = useRef<{
    data: typeof data;
    salesRepConfig: SalesRepConfig;
  } | null>(null);

  useEffect(() => {
    if (loading || !data || paramsRef.current === null || !isFocused) {
      return;
    }

    if (syncedDataRef.current !== data) {
      const params = paramsRef.current;
      const cartData = { getOrderEntryItemsFromCart: data.cart };
      const orderGuide = data.orderGuide;
      const orderEntryData = {
        getOrderEntryItemsFromOrderGuide: orderGuide,
      };
      const headers = (orderGuide?.price_edit_display_headers ??
        EMPTY_HEADERS) as OrderEntryPriceEditDisplayHeader[];

      synchronizeOrderEntryCollections(orderEntryData);
      synchronizeCartStore(params, cartData, headers);

      syncedDataRef.current = data;
      setIsSyncing(false);
    }

    if (
      salesRepConfig &&
      (syncedEditorsRef.current?.data !== data ||
        syncedEditorsRef.current?.salesRepConfig !== salesRepConfig)
    ) {
      synchronizePriceEditors(
        data.orderGuide?.order_entry_items ?? [],
        salesRepConfig,
      );
      syncedEditorsRef.current = { data, salesRepConfig };
    }
  }, [
    data,
    isFocused,
    loading,
    salesRepConfig,
    synchronizeOrderEntryCollections,
    synchronizeCartStore,
    synchronizePriceEditors,
  ]);

  const { data: tableData } = useLiveQuery(
    (q) =>
      orderGuideUUID
        ? q
            .from({ orderEntryCollection })
            .where(({ orderEntryCollection }) =>
              linkedSupplierCollectionUUID
                ? or(
                    inArray(
                      orderGuideUUID,
                      orderEntryCollection.orderGuideCollectionIds,
                    ),
                    inArray(
                      linkedSupplierCollectionUUID,
                      orderEntryCollection.orderGuideCollectionIds,
                    ),
                  )
                : inArray(
                    orderGuideUUID,
                    orderEntryCollection.orderGuideCollectionIds,
                  ),
            )
            .orderBy(
              ({ orderEntryCollection }) => orderEntryCollection.sortRank,
              'asc',
            )
            .orderBy(
              ({ orderEntryCollection }) => orderEntryCollection.name,
              'asc',
            )
        : null,
    [orderGuideUUID, linkedSupplierCollectionUUID],
  );

  // Memoized so consumers that compare by reference (e.g. effects writing to a
  // store) don't fire on every render due to the `?? EMPTY_HEADERS` fallback.
  const priceEditDisplayHeaders = useMemo<OrderEntryPriceEditDisplayHeader[]>(
    () =>
      (data?.orderGuide?.price_edit_display_headers ??
        EMPTY_HEADERS) as OrderEntryPriceEditDisplayHeader[],
    [data?.orderGuide?.price_edit_display_headers],
  );

  return {
    error,
    loading: loading || networkStatus === NetworkStatus.refetch || isSyncing,
    priceEditDisplayHeaders,
    refetch,
    tableData,
  };
}

const EMPTY_HEADERS: OrderEntryPriceEditDisplayHeader[] = [];
